import { ApolloCache, TypePolicy } from '@apollo/client';
import {
  DEFAULT_PINNED_VIEWS_QUERY_NAME,
  PortalDefaultViewsResponseType,
} from '../portalViews/hooks/useGetPortalDefaultViews';
import { SingleViewByIdQuery } from '../singleView/hooks/useFetchSingleView';
import { VIEWS_SEARCH_QUERY_NAME } from '../viewsSearch/hooks/useFetchViewsSearch';
import { View } from '../viewsSearch/types/ViewTypes';
import {
  ViewsByCategoryQueryType,
  ViewsByCategoryResponseType,
  ViewsByCategorySearchQueries,
  getViewsByCatagoryQueryNameForCategory,
} from '../standardViews/hooks/useFetchViewsByCategory';
import {
  SupportedCategories,
  VIEW_CATEGORY_MINE,
} from '../addView/ViewCategories';
import { CREATE_SINGLE_VIEW_MUTATION_NAME } from '../createView/hooks/useCreateSingleViewMutation';
import { DEFAULT_PINNED_VIEW_QUERY_NAME } from '../pinnedViews/hooks/useFetchDefaultPinnedView';

export const mergeUnique = <T extends { id: string | number }>({
  existing,
  incoming,
}: {
  existing?: T[];
  incoming?: T[];
}): T[] | undefined => {
  if (!existing?.length) {
    return incoming;
  }
  if (!incoming?.length) {
    return existing;
  }

  const existingMap = new Map(existing.map((view) => [view.id, view]));
  const incomingMap = new Map(incoming.map((view) => [view.id, view]));
  const uniqueIdOrder = [
    ...new Set([
      ...existing.map(({ id }) => id),
      ...incoming.map(({ id }) => id),
    ]),
  ];

  // If there is any overlap between existing and incoming,
  // maintain the order in existing
  // but prefer the incoming data as it is more up to date
  const merged = uniqueIdOrder
    .map((id) => incomingMap.get(id) || existingMap.get(id))
    .filter(<Defined>(x: Defined | undefined): x is Defined => Boolean(x));
  return merged;
};

const viewsByCategoryKeyArgs = [
  'query',
  'objectTypeId',
  'namespace',
] as const satisfies readonly (keyof ViewsByCategoryQueryType)[];

export const mutationFieldPolicies: TypePolicy['fields'] = {
  [CREATE_SINGLE_VIEW_MUTATION_NAME]: {
    merge(__, incoming: View, { cache }) {
      cache.updateQuery(
        {
          query: ViewsByCategorySearchQueries[VIEW_CATEGORY_MINE],
          variables: {
            objectTypeId: incoming.objectTypeId,
            query: '',
            category: VIEW_CATEGORY_MINE,
            namespace: incoming.namespaceName,
          },
        },
        (data) => {
          const currentViewsByCategory =
            data?.[getViewsByCatagoryQueryNameForCategory(VIEW_CATEGORY_MINE)];

          if (!currentViewsByCategory?.viewItems || !incoming?.id) {
            return data;
          }

          return {
            [getViewsByCatagoryQueryNameForCategory(VIEW_CATEGORY_MINE)]: {
              ...currentViewsByCategory,
              total: currentViewsByCategory.total + 1,
              // Treat the incoming view as the "next page" of data coming in
              // and let the merge policy for SupportedCategories handle
              // merging it into the existing data
              viewItems: [incoming],
            },
          };
        }
      );
      return incoming;
    },
  },
};

export const queryFieldPolicies: TypePolicy['fields'] = {
  [VIEWS_SEARCH_QUERY_NAME]: {
    merge(__, incoming, { cache }) {
      incoming.results.forEach((view: View) => {
        cache.writeQuery({
          data: { singleViewById: view },
          query: SingleViewByIdQuery,
          variables: {
            id: view.id,
            objectTypeId: view.objectTypeId,
            namespaceName: view.namespaceName,
          },
        });
      });
      return incoming;
    },
  },
  [DEFAULT_PINNED_VIEWS_QUERY_NAME]: {
    merge(__, incoming: PortalDefaultViewsResponseType, { cache }) {
      (incoming || []).forEach((view: View) => {
        cache.writeQuery({
          data: { singleViewById: view },
          query: SingleViewByIdQuery,
          variables: {
            id: view.id,
            objectTypeId: view.objectTypeId,
            namespaceName: view.namespaceName,
          },
        });
      });
      return incoming;
    },
  },
  [DEFAULT_PINNED_VIEW_QUERY_NAME]: {
    merge(__, incoming: View, { cache }) {
      cache.writeQuery({
        data: { singleViewById: incoming },
        query: SingleViewByIdQuery,
        variables: {
          id: incoming.id,
          objectTypeId: incoming.objectTypeId,
          namespaceName: incoming.namespaceName,
        },
      });
      return incoming;
    },
  },
  ...SupportedCategories.reduce(
    (policies, category) => ({
      /* eslint-disable-next-line hubspot-dev/no-reduce-accumulator-copy */
      ...policies,
      [getViewsByCatagoryQueryNameForCategory(category)]: {
        keyArgs: viewsByCategoryKeyArgs,
        merge(
          existing: ViewsByCategoryResponseType,
          incoming: ViewsByCategoryResponseType,
          { cache }: { cache: ApolloCache<any> }
        ) {
          (incoming && incoming.viewItems ? incoming.viewItems : []).forEach(
            (view: View) => {
              cache.writeQuery({
                data: { singleViewById: view },
                query: SingleViewByIdQuery,
                variables: {
                  id: view.id,
                  objectTypeId: view.objectTypeId,
                  namespaceName: view.namespaceName,
                },
              });
            }
          );

          const uniqueMergedViewItems = mergeUnique({
            existing: existing?.viewItems,
            incoming: incoming?.viewItems,
          });
          return {
            ...incoming,
            viewItems: uniqueMergedViewItems,
          };
        },
      },
    }),
    {}
  ),
};
